import React, { useMemo } from 'react';
import { CONNECTOR_SERVICES } from 'js/data/connectors';

import { Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { Link, useRouteMatch, useParams } from 'react-router-dom';
import { Icon, Loading } from 'js/components';
import TeamForm from './TeamForm';
import TeamMembers from './TeamMembers';
import ServiceCard from './ServiceCard';
import styles from './TeamSettings.module.scss';
import { faPlug } from '@fortawesome/pro-regular-svg-icons';
import { faGear } from '@fortawesome/pro-solid-svg-icons';

function ConnectorConfigurations({ teamConnectorConfigurations }) {
  const match = useRouteMatch();
  const extendedConfigurations = teamConnectorConfigurations.map(
    (configuration) => {
      const service = CONNECTOR_SERVICES.find(
        (service) => service.id === configuration?.connector?.type
      );

      return {
        configuration,
        service,
      };
    }
  );

  return (
    <div className={styles['services-list']}>
      {extendedConfigurations.map(({ service, configuration }) => (
        <ServiceCard
          to={`${match.url}/connectors/${configuration.connector.id}`}
          key={configuration.connector.id}
          service={service}
          connector={configuration.connector}
        />
      ))}
    </div>
  );
}

function TeamSettings({
  team,
  onUpdate,
  onAddTeamMember,
  onUpdateTeamMember,
  onRemoveTeamMember,
  connectors = [],
  teamConnectorConfigurations = [],
  connectorsHasLoaded,
  teamConnectorConfigurationsHasLoaded,
  cancelUrl,
}) {
  const match = useRouteMatch();
  const { workspaceId } = useParams();
  const hasConnectors = connectors && connectors.length > 0;
  const hasConnectorConfigurations =
    teamConnectorConfigurations && teamConnectorConfigurations.length > 0;

  const addConnectorLinkTitle = useMemo(() => {
    if (!connectorsHasLoaded || !teamConnectorConfigurationsHasLoaded) {
      return null;
    } else if (connectors?.length === 0) {
      return 'Add a connector to the workspace to get started';
    } else if (connectors?.length === teamConnectorConfigurations?.length) {
      return 'You have configured all connectors for this workspace';
    }
    return null;
  }, [
    connectorsHasLoaded,
    teamConnectorConfigurationsHasLoaded,
    connectors,
    teamConnectorConfigurations,
  ]);

  return (
    <>
      <Card>
        <CardBody>
          <TeamForm
            submitLabel="Update"
            team={team}
            onSubmit={onUpdate}
            cancelUrl={cancelUrl}
          />
        </CardBody>
      </Card>
      <TeamMembers
        team={team}
        onAddTeamMember={onAddTeamMember}
        onUpdateTeamMember={onUpdateTeamMember}
        onRemoveTeamMember={onRemoveTeamMember}
      />
      <Card>
        <CardHeader>
          <div className="action-items">
            <Link
              to={`${match.url}/connectors/new`}
              className="btn btn-primary"
              title={addConnectorLinkTitle}
              disabled={
                !connectorsHasLoaded ||
                !teamConnectorConfigurationsHasLoaded ||
                addConnectorLinkTitle !== null
              }
            >
              <Icon icon={faGear} className="btn-icon-left" />
              Configure connector
            </Link>
          </div>
          <CardTitle tag="h5">Connectors</CardTitle>
        </CardHeader>
        <CardBody>
          {connectorsHasLoaded && teamConnectorConfigurationsHasLoaded ? (
            hasConnectors ? (
              <div>
                {hasConnectorConfigurations ? (
                  <ConnectorConfigurations
                    teamConnectorConfigurations={teamConnectorConfigurations}
                  />
                ) : (
                  <div className="media text-muted">
                    <Icon icon={faPlug} size="4x" className="mr-3" fixedWidth />
                    <div className="media-body">
                      <p>
                        You haven't configured any connectors for this team yet.
                      </p>
                      <p>
                        Configuring connectors lets you integrate data from
                        external systems to get a better picture of your team's
                        capabilities.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="media text-muted">
                <Icon icon={faPlug} size="4x" className="mr-3" fixedWidth />
                <div className="media-body">
                  <p>
                    This workspace doesn't have any connectors configured yet.
                  </p>
                  <p>
                    Configuring connectors lets you integrate data from external
                    systems to get a better picture of your team's capabilities.
                  </p>
                  <p>
                    <Link
                      to={`/w/${workspaceId}/settings/connectors/new`}
                      className="btn btn-outline-primary"
                    >
                      <Icon icon={faPlug} className="btn-icon-left" />
                      Set up a new connector
                    </Link>
                  </p>
                </div>
              </div>
            )
          ) : (
            <Loading />
          )}
        </CardBody>
      </Card>
    </>
  );
}

export default TeamSettings;
